import React, { useEffect, useState } from "react";
import { useMediaQuery } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import {
  getCollectPublicationYear,
  getProjectDefaultPubDateFormat,
  getFieldOptions,
  getStepErrors,
  getStepFields,
} from "../../common/selectors";
import { anyNotBlank, isBlank, notBlank } from "../../common/general";
import { FIELD_BLURRED, FIELD_CHANGED } from "../fileDetailsActions";
import * as steps from "common/steps";
import { firstError, hasError } from "common/validation";

import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import DatePicker from "../../general-components/DatePicker";
import CustomizableTextField from "../../general-components/CustomizableTextField";
import { useTranslation } from "react-i18next";
import { isRequired } from "../../common/validation";

const PublicationDate = ({ xs = true, sm = true, md = true }) => {
  const { t } = useTranslation();
  const isSm = useMediaQuery(theme => theme.breakpoints.up("sm"));
  const isMd = useMediaQuery(theme => theme.breakpoints.up("md"));

  const errors = useSelector(getStepErrors(steps.FILE_DETAILS));
  const fields = useSelector(getStepFields(steps.FILE_DETAILS));
  const fieldOptions = useSelector(getFieldOptions);
  const collectPublicationYear = useSelector(getCollectPublicationYear);
  const defaultPubDateFormat = useSelector(getProjectDefaultPubDateFormat);

  const dispatch = useDispatch();
  const onBlur = (field, value) => () =>
    dispatch({ type: FIELD_BLURRED, field, value });
  const onFieldChange = path => event => {
    if (notBlank(event)) {
      const value = notBlank(event.target) ? event.target.value : event;
      dispatch({ type: FIELD_CHANGED, path, value });
    }
  };

  const onDateChange = dateFormat => event => {
    let date;
    let year;
    if (notBlank(event)) {
      const value = notBlank(event.target) ? event.target.value : event;

      if (dateFormat === "publicationDate") {
        date = value.toFormat("yyyy-MM-dd");
        year = value.year.toString();
      } else {
        date = `${value}-01-01`;
        year = value;
      }

      onFieldChange(["publicationDate"])(date);
      onFieldChange(["publicationYear"])(year);
    }
  };

  const [show, setShow] = useState(true);
  useEffect(() => {
    let visible = false;

    if (
      anyNotBlank([
        fieldOptions["publication_year"],
        fieldOptions["publication_date"],
      ])
    ) {
      visible =
        !fieldOptions["publication_year"].hidden ||
        !fieldOptions["publication_date"].hidden;
    }

    setShow(visible || collectPublicationYear);
  }, [fieldOptions, collectPublicationYear]);

  const [fullDate, setFullDate] = useState(true);
  useEffect(() => {
    if (notBlank(defaultPubDateFormat)) {
      setFullDate(defaultPubDateFormat === "date");
    }
  }, [defaultPubDateFormat]);

  if (!show) {
    return null;
  }

  return (
    <Grid
      container
      item
      xs={12}
      sm={isBlank(defaultPubDateFormat) ? 12 : 6}
      md={6}
    >
      {isBlank(defaultPubDateFormat) && (
        <Grid item>
          <Box mt={isSm ? "16px" : "26px"} pr="8px">
            <Select
              value={fullDate ? "pd" : "py"}
              onChange={event => {
                // Reset both fields on field change
                setFullDate(event.target.value === "pd");
                onFieldChange(["publicationDate"])({ target: { value: null } });
                onFieldChange(["publicationYear"])({ target: { value: null } });
              }}
              inputProps={{
                name: "content-full-date-select",
                id: "content-full-date-select",
              }}
            >
              <MenuItem value={"py"}>
                {isMd ? "Publication Year" : "Pub. Year"}
              </MenuItem>
              <MenuItem value={"pd"}>
                {isMd ? "Publication Date" : "Pub. Date"}
              </MenuItem>
            </Select>
          </Box>
        </Grid>
      )}

      {fullDate ? (
        <DatePicker
          xs={xs}
          sm={sm}
          md={md}
          marginTop={isSm ? 0 : "10px"}
          placeholder="mm/dd/yyyy"
          required={isRequired("publication_date", fieldOptions)}
          value={fields.publicationDate || ""}
          onBlur={onBlur("publicationDate", fields.publicationDate)}
          onChange={onDateChange("publicationDate")}
          error={hasError(errors, "publicationDate")}
          helperText={t(firstError(errors, "publicationDate"))}
        />
      ) : (
        <CustomizableTextField
          name="publicationYear"
          fieldOption="publication_year"
          marginTop={isSm ? 0 : "10px"}
          xs={xs}
          sm={sm}
          md={md}
          defaultLabel={t("file_details.publication_year", "Year published")}
          value={fields.publicationYear || ""}
          onChange={onDateChange("publicationYear")}
          error={hasError(errors, "publicationYear")}
          helperText={t(firstError(errors, "publicationYear"))}
          onBlur={onBlur("publicationYear", fields.publicationYear)}
          inputProps={{
            maxLength: 4,
            inputMode: "numeric",
            pattern: "[0-9]{1,4}",
          }}
        />
      )}
    </Grid>
  );
};

export default PublicationDate;
